import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, TextField, 
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Tooltip, Alert, Tabs, Tab, Avatar, InputAdornment,
  FormControlLabel, Checkbox
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, 
  Visibility as ViewIcon, Download as DownloadIcon, AttachMoney as MoneyIcon,
  People as PeopleIcon, Assessment as AssessmentIcon, Calculate as CalculateIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';
import useCurrentUser from '../../../utils/useCurrentUser';

const SalaryManagementPage = () => {
  const { t } = useTranslation();
  // Context-aware cache keys
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  const { user, permissions, can } = useCurrentUser();

  const [positions, setPositions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [monthlyReportMonth, setMonthlyReportMonth] = useState('');
  const [bulkMonth, setBulkMonth] = useState('');
  const [bulkTaxRate, setBulkTaxRate] = useState('');

  const cacheKeyStaff = `finance:staff:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}|role:${selectedRoleId || 'none'}`;
  const cacheKeyPayroll = `finance:payroll:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  
  // Additional permission checks using utility functions
  // NOTE: /api/auth/me currently returns allowed *page routes* (e.g. '/salary-management'),
  // not granular action permissions like 'manage_payroll'.
  // So we gate salary-management actions by page access.
  const roleKey = String(user?.roleNormalized || user?.role || '').trim();
  const roleAllowsSalaryManagement = ['SchoolAdmin'].includes(roleKey);
  const canUseSalaryManagement = (Array.isArray(permissions) && permissions.includes('/salary-management')) || roleAllowsSalaryManagement;
  const canCreateStaff = canUseSalaryManagement;
  const canEditStaff = canUseSalaryManagement;
  const canDeleteStaff = canUseSalaryManagement;
  const canProcessPayroll = canUseSalaryManagement;
  const [activeTab, setActiveTab] = useState(0);
  // Seed from cache
  const cachedStaff = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyStaff) || '[]'); } catch { return []; } })();
  const cachedPayroll = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyPayroll) || '[]'); } catch { return []; } })();

  const [staff, setStaff] = useState(Array.isArray(cachedStaff) ? cachedStaff : []);
  const [payrollRecords, setPayrollRecords] = useState(Array.isArray(cachedPayroll) ? cachedPayroll : []);
  const [loading, setLoading] = useState(true);
  const guardRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [payrollStaffOptions, setPayrollStaffOptions] = useState([]);
  const [payrollStaffLoading, setPayrollStaffLoading] = useState(false);
  
  const [staffDialog, setStaffDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffDialogRoleId, setStaffDialogRoleId] = useState('');
  const [staffDialogOptions, setStaffDialogOptions] = useState([]);
  const [staffForm, setStaffForm] = useState({
    userId: '',
    name: '',
    position: '',
    employmentType: 'PERMANENT',
    basicSalary: '',
    hourlyRate: '',
    mtnMomoPhone: '',
    orangeMoneyPhone: '',
    status: 'active'
  });

  // Bulk payout state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [provider, setProvider] = useState('OFFLINE');
  const [payoutAvailability, setPayoutAvailability] = useState({ mtn: false, orange: false });

  // Helpers for selection
  const getStaffRowId = (member) => {
    const rid = member?.id ?? member?.userId ?? member?.staffId;
    if (rid === null || rid === undefined) return '';
    const s = String(rid);
    return s === 'undefined' || s === 'null' ? '' : s;
  };

  const toggleSelectAll = (checked) => {
    if (checked && Array.isArray(staff)) {
      setSelectedIds(
        staff
          .map((s) => getStaffRowId(s))
          .filter(Boolean)
      );
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectOne = (id) => {
    const sid = String(id);
    setSelectedIds((prev) => (
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    ));
  };

  const totalSelectedAmount = selectedIds.reduce((sum, id) => {
    const m = staff.find((s) => String(getStaffRowId(s)) === String(id));
    return sum + (m?.basicSalary || 0);
  }, 0);

  // Currency formatter for XAF (Cameroon)
  const formatXAF = (amount) => new Intl.NumberFormat('en-CM', {
    style: 'currency',
    currency: 'XAF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

  const normalizeNumberInput = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    const n = Number(raw);
    return Number.isFinite(n) ? n : '';
  };

  const normalizeCameroonPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) return '';
    return digits.length >= 9 ? digits.slice(-9) : digits;
  };

  const detectMomoCarrier = (rawPhone) => {
    const phone = normalizeCameroonPhone(rawPhone);
    if (phone.length < 2) return null;

    const p2 = phone.slice(0, 2);
    const p3 = Number(phone.slice(0, 3));

    const isMtn =
      p2 === '67' ||
      p2 === '68' ||
      (Number.isFinite(p3) && p3 >= 650 && p3 <= 654);

    const isOrange =
      p2 === '69' ||
      (Number.isFinite(p3) && p3 >= 655 && p3 <= 659) ||
      (Number.isFinite(p3) && p3 >= 686 && p3 <= 689);

    if (isMtn) return { carrier: 'MTN', phone };
    if (isOrange) return { carrier: 'ORANGE', phone };
    return null;
  };

  const [payrollDialog, setPayrollDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [payrollForm, setPayrollForm] = useState({
    staffId: '',
    month: '',
    approvedHoursWorked: '',
    taxRate: '',
    manualDeductions: ''
  });

  const payrollStaffSource = Array.isArray(selectedRoleId ? staff : payrollStaffOptions)
    ? (selectedRoleId ? staff : payrollStaffOptions)
    : [];

  useEffect(() => {
    // 2s guard if no cache
    const hasCache = (Array.isArray(staff) && staff.length > 0) || (Array.isArray(payrollRecords) && payrollRecords.length > 0);
    if (!hasCache) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    fetchData();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchJson('/api/payroll/payout-availability', { method: 'GET' });
        const avail = resp?.data || {};
        setPayoutAvailability({ mtn: !!avail.mtn, orange: !!avail.orange });
      } catch (_) {
        setPayoutAvailability({ mtn: false, orange: false });
      }
    })();
  }, []);

  useEffect(() => {
    if (provider === 'MTN' && !payoutAvailability.mtn) setProvider('OFFLINE');
    if (provider === 'ORANGE' && !payoutAvailability.orange) setProvider('OFFLINE');
  }, [provider, payoutAvailability.mtn, payoutAvailability.orange]);

  useEffect(() => {
    if (!selectedRoleId) {
      setStaff([]);
      setSelectedIds([]);
      return;
    }
    fetchStaff();
    setSelectedIds([]);
  }, [selectedRoleId]);

  useEffect(() => {
    if (monthlyReportMonth) return;
    const d = new Date();
    setMonthlyReportMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }, [monthlyReportMonth]);

  useEffect(() => {
    if (bulkMonth) return;
    const d = new Date();
    setBulkMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }, [bulkMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [positionsRes, payrollRes] = await Promise.allSettled([
        fetchJson('/api/payroll/positions'),
        // '/api/payroll/records', not '/payroll': the latter is gated on the
        // '/payroll' permission, which the page catalogue does not offer. A role
        // granted only Salary Management got a 403 here while the rest of the
        // page loaded, so the payroll history came back empty for no visible
        // reason.
        fetchJson('/api/payroll/records'),
      ]);

      const positionsData = positionsRes.status === 'fulfilled' ? positionsRes.value : null;
      const roles = (() => {
        if (positionsData && positionsData.success && Array.isArray(positionsData.data)) return positionsData.data;
        if (positionsData && Array.isArray(positionsData.data)) return positionsData.data;
        if (Array.isArray(positionsData)) return positionsData;
        return [];
      })();
      setPositions(roles);

      const payrollData = payrollRes.status === 'fulfilled' ? payrollRes.value : null;
      const payrollRows = (() => {
        if (Array.isArray(payrollData)) return payrollData;
        if (payrollData && Array.isArray(payrollData.rows)) return payrollData.rows;
        if (payrollData && Array.isArray(payrollData.data)) return payrollData.data;
        return [];
      })();
      setPayrollRecords(payrollRows);

      if (!selectedRoleId) {
        setStaff([]);
      }
      try {
        localStorage.setItem(cacheKeyPayroll, JSON.stringify(payrollRows));
      } catch(_) {}
    } catch (err) {
      setError(t('salaryPage.errors.loadData'));
      console.error('Fetch data error:', err);
      // Set empty arrays as fallback to prevent filter errors
      setStaff([]);
      setPayrollRecords([]);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollStaffOptions = async () => {
    try {
      setPayrollStaffLoading(true);
      const staffData = await fetchJson('/api/payroll/staff');
      const staffRows = (() => {
        if (Array.isArray(staffData)) return staffData;
        if (staffData && Array.isArray(staffData.rows)) return staffData.rows;
        if (staffData && Array.isArray(staffData.data)) return staffData.data;
        if (staffData && Array.isArray(staffData.staff)) return staffData.staff;
        if (staffData && staffData.success && Array.isArray(staffData.data)) return staffData.data;
        return [];
      })();
      setPayrollStaffOptions(staffRows);
    } catch (e) {
      console.error('Fetch payroll staff options error:', e);
      setPayrollStaffOptions([]);
    } finally {
      setPayrollStaffLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const roleIdNum = Number(selectedRoleId);
      const staffData = await fetchJson(`/api/payroll/staff?roleId=${encodeURIComponent(String(roleIdNum))}`);
      const staffRows = (() => {
        if (Array.isArray(staffData)) return staffData;
        if (staffData && Array.isArray(staffData.rows)) return staffData.rows;
        if (staffData && Array.isArray(staffData.data)) return staffData.data;
        if (staffData && Array.isArray(staffData.staff)) return staffData.staff;
        if (staffData && staffData.success && Array.isArray(staffData.data)) return staffData.data;
        return [];
      })();
      setStaff(staffRows);
      try {
        localStorage.setItem(cacheKeyStaff, JSON.stringify(staffRows));
      } catch (_) {}
    } catch (e) {
      console.error('Fetch staff error:', e);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSubmit = async () => {
    try {
      const userId = editingStaff?.id || staffForm?.userId;
      if (!userId) {
        setError(t('salaryPage.errors.selectStaff'));
        return;
      }

      await fetchJson(`/api/payroll/staff/${userId}/config`, {
        method: 'PUT',
        body: {
          employmentType: staffForm.employmentType,
          basicSalary: staffForm.basicSalary,
          hourlyRate: staffForm.hourlyRate,
          mtnMomoPhone: staffForm.mtnMomoPhone,
          orangeMoneyPhone: staffForm.orangeMoneyPhone,
          status: staffForm.status,
        }
      });
      
      setStaffDialog(false);
      setEditingStaff(null);
      resetStaffForm();
      fetchStaff();
    } catch (err) {
      setError(t('salaryPage.errors.saveSalary'));
    }
  };

  const handlePayrollSubmit = async () => {
    try {
      const staffId = selectedStaff?.id || payrollForm?.staffId;
      if (!staffId) {
        setError(t('salaryPage.errors.selectStaff'));
        return;
      }
      if (!payrollForm.month) {
        setError(t('salaryPage.errors.selectMonth'));
        return;
      }

      const et = String((selectedStaff?.employmentType || '').toUpperCase());
      const isPartTime = et === 'PART_TIME';

      if (isPartTime) {
        const hours = payrollForm.approvedHoursWorked;
        if (hours === '' || hours === null || hours === undefined || !Number.isFinite(Number(hours))) {
          setError(t('salaryPage.errors.hoursRequired'));
          return;
        }
      }
      const body = {
        staffId,
        month: payrollForm.month,
        approvedHoursWorked: isPartTime ? payrollForm.approvedHoursWorked : undefined,
        taxRate: payrollForm.taxRate,
        manualDeductions: payrollForm.manualDeductions,
      };

      await fetchJson('/api/payroll/process', {
        method: 'POST',
        body
      });
      
      setPayrollDialog(false);
      resetPayrollForm();
      fetchData();
    } catch (err) {
      setError(t('salaryPage.errors.savePayroll'));
    }
  };

  const resetStaffForm = () => {
    setStaffForm({
      userId: '',
      name: '',
      position: '',
      employmentType: 'PERMANENT',
      basicSalary: '',
      hourlyRate: '',
      mtnMomoPhone: '',
      orangeMoneyPhone: '',
      status: 'active'
    });
  };

  const resetPayrollForm = () => {
    setPayrollForm({
      staffId: '',
      month: '',
      approvedHoursWorked: '',
      taxRate: '',
      manualDeductions: ''
    });
  };

  const resolveAccessToken = () => {
    const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token', 'ACCESS_TOKEN'];
    for (const k of tokenKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
    try {
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (raw) {
        const obj = JSON.parse(raw);
        const candidate = obj?.accessToken || obj?.token || obj?.authToken || obj?.jwt || obj?.id_token;
        if (candidate) return candidate;
      }
    } catch (_) {}
    return null;
  };

  const resolveApiBaseUrl = () => {
    const baseRaw =
      (typeof process !== 'undefined' && process?.env?.REACT_APP_API_BASE_URL)
        ? process.env.REACT_APP_API_BASE_URL
        : 'http://localhost:5000';
    return (baseRaw || '').toString().replace(/\/$/, '');
  };

  const buildApiUrl = (path) => {
    const base = resolveApiBaseUrl();
    const isAbsolute = /^https?:\/\//i.test(path);
    if (isAbsolute) return path;

    let basePart = base;
    let pathPart = path.startsWith('/') ? path : `/${path}`;
    const baseEndsWithApi = /\/api$/i.test(basePart);
    const pathStartsWithApi = /^\/api(\b|\/)/i.test(pathPart);
    if (baseEndsWithApi && pathStartsWithApi) {
      pathPart = pathPart.replace(/^\/api/i, '');
    }
    return `${basePart}${pathPart}`;
  };

  const downloadMonthlyPayrollPdf = async () => {
    try {
      if (!monthlyReportMonth) {
        setError(t('salaryPage.errors.selectMonth'));
        return;
      }

      const [y, m] = String(monthlyReportMonth).split('-');
      const params = new URLSearchParams({ month: String(m), year: String(y) });

      const token = resolveAccessToken();
      const headers = { Accept: 'application/pdf' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const sid = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      if (sid) {
        headers['X-Scope-School-Id'] = String(sid);
        headers['X-School-Id'] = String(sid);
      }
      const activePhase = localStorage.getItem('schoolAdmin_activePhase') || '';
      const activePhaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      if (activePhase) headers['X-Active-Phase'] = String(activePhase);
      if (activePhaseId) headers['X-Active-Phase-Id'] = String(activePhaseId);
      const namespacedKey = sid ? `${sid}:currentAcademicYearId` : 'currentAcademicYearId';
      const ay = localStorage.getItem(namespacedKey) || localStorage.getItem('currentAcademicYearId') || '';
      if (ay) headers['X-Academic-Year-Id'] = String(ay);

      const requestUrl = buildApiUrl(`/api/payroll/monthly-report.pdf?${params.toString()}`);
      const response = await fetch(requestUrl, { headers, credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        const msg = err?.error || err?.message || `Failed to download PDF (${response.status})`;
        throw new Error(msg);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `payroll-${y}-${m}.pdf`;

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Monthly payroll PDF download error:', e);
      setError(e?.message || t('salaryPage.errors.downloadPdf'));
    }
  };

  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
  };

  // Removed hard access restriction. Specific controls remain permission-gated via `can.*` flags below.

  if (loading) {
    const hasCache = (Array.isArray(staff) && staff.length > 0) || (Array.isArray(payrollRecords) && payrollRecords.length > 0);
    if (!hasCache) {
      return (
        <AppLoader
          variant="circular3"
          size={58}
          centered
          minHeight="50vh"
          messages={[t('salaryPage.loading'), t('salaryPage.applyingPhase'), t('salaryPage.applyingAY')]}
        />
      );
    }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('salaryPage.title')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('salaryPage.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CalculateIcon />}
            sx={{ mr: 1 }}
            onClick={() => {
              if (!canProcessPayroll) {
                setError(t('salaryPage.errors.noAccess'));
                return;
              }
              setSelectedStaff(null);
              resetPayrollForm();
              setPayrollDialog(true);
              if (!selectedRoleId) {
                fetchPayrollStaffOptions();
              }
            }}
            disabled={false}
          >
            {t('salaryPage.processPayroll')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={async () => {
              if (!canCreateStaff) {
                setError(t('salaryPage.errors.noAccessStaff'));
                return;
              }

              // If positions failed to load earlier, retry so the dialog isn't blocked by a disabled button.
              if (!Array.isArray(positions) || positions.length === 0) {
                try {
                  const positionsData = await fetchJson('/api/payroll/positions');
                  const roles = (() => {
                    if (positionsData && positionsData.success && Array.isArray(positionsData.data)) return positionsData.data;
                    if (positionsData && Array.isArray(positionsData.data)) return positionsData.data;
                    if (Array.isArray(positionsData)) return positionsData;
                    return [];
                  })();
                  setPositions(roles);
                } catch (e) {
                  setError(e?.message || t('salaryPage.errors.loadPositions'));
                }
              }

              setEditingStaff(null);
              resetStaffForm();
              const nextRoleId = selectedRoleId || '';
              setStaffDialogRoleId(nextRoleId);
              setStaffDialogOptions(Array.isArray(staff) ? staff : []);
              setStaffDialog(true);
            }}
            disabled={false}
          >
            {t('salaryPage.addStaffPayroll')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label={t('salaryPage.tabs.overview')} icon={<AssessmentIcon />} />
          <Tab label={t('salaryPage.tabs.staffSalaries')} icon={<PeopleIcon />} />
          <Tab label={t('salaryPage.tabs.payroll')} icon={<MoneyIcon />} />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('salaryPage.overview.totalStaff')}
                </Typography>
                <Typography variant="h4">
                  {staff.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('salaryPage.overview.active', { count: Array.isArray(staff) ? staff.filter(s => s.status === 'active').length : 0 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('salaryPage.overview.monthlyPayroll')}
                </Typography>
                <Typography variant="h4">
                  {formatXAF(payrollRecords
                    .filter(p => p.status === 'processed')
                    .reduce((sum, p) => sum + (p.netSalary || 0), 0))}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('salaryPage.overview.thisMonth')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('salaryPage.overview.averageSalary')}
                </Typography>
                <Typography variant="h4">
                  {formatXAF(Array.isArray(staff) && staff.length > 0 ? 
                    Math.round(staff.reduce((sum, s) => sum + (s.basicSalary || 0), 0) / staff.length) : 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('salaryPage.overview.perStaff')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('salaryPage.overview.pendingPayroll')}
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {payrollRecords.filter(p => p.status === 'pending').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('salaryPage.overview.requireProcessing')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>{t('salaryPage.staff.title')}</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="position-filter-label">{t('salaryPage.staff.position')}</InputLabel>
                <Select
                  labelId="position-filter-label"
                  label={t('salaryPage.staff.position')}
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                >
                  {Array.isArray(positions) && positions.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!selectedRoleId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('salaryPage.staff.selectPosition')}
            </Alert>
          )}

          {selectedRoleId && (
            <>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 1 }}>
                <Box>
                  {(() => {
                    const eligibleIds = Array.isArray(staff)
                      ? staff
                        .map((s) => getStaffRowId(s))
                        .filter(Boolean)
                      : [];
                    const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.includes(id));
                    const someSelected = eligibleIds.some((id) => selectedIds.includes(id));
                    return (
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                      />
                    )}
                    label={t('salaryPage.staff.selectAll')}
                  />
                    );
                  })()}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Chip label={t('salaryPage.staff.selected', { count: selectedIds.length })} />
                  <Chip color="primary" label={t('salaryPage.staff.total', { amount: formatXAF(totalSelectedAmount) })} />
                  <Button variant="contained" disabled={selectedIds.length === 0} onClick={() => setBulkDialog(true)}>
                    {t('salaryPage.staff.paySelected')}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>{t('salaryPage.table.staffMember')}</TableCell>
                    <TableCell>{t('salaryPage.table.position')}</TableCell>
                    <TableCell>{t('salaryPage.table.salary')}</TableCell>
                    <TableCell>{t('salaryPage.table.status')}</TableCell>
                    <TableCell>{t('salaryPage.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(staff) && staff.length > 0 ? (
                    staff.map((member) => (
                      <TableRow key={getStaffRowId(member) || member.id}>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={selectedIds.includes(getStaffRowId(member))}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSelectOne(getStaffRowId(member))}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2 }}>{member.name?.charAt(0) || 'S'}</Avatar>
                            <Box>
                              <Typography variant="subtitle2">{member.name}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {member.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{member.position}</TableCell>
                        <TableCell>
                          {String(member.employmentType || '').toUpperCase() === 'PART_TIME'
                            ? `${formatXAF(member.hourlyRate || 0)} / hr`
                            : formatXAF(member.basicSalary || 0)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.staffStatus || member.status}
                            color={getStatusColor(member.staffStatus || member.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Tooltip title={t('salaryPage.tooltips.editSalary')}>
                              <IconButton size="small" onClick={() => {
                                setEditingStaff(member);
                                setStaffForm({
                                  userId: member.id,
                                  name: member.name || '',
                                  position: member.position || '',
                                  employmentType: member.employmentType || 'PERMANENT',
                                  basicSalary: member.basicSalary || '',
                                  hourlyRate: member.hourlyRate || '',
                                  mtnMomoPhone: member.mtnMomoPhone || '',
                                  orangeMoneyPhone: member.orangeMoneyPhone || '',
                                  status: member.staffStatus || member.status || 'active',
                                });
                                setStaffDialog(true);
                              }}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('salaryPage.tooltips.processPayroll')}>
                              <IconButton size="small" color="primary" onClick={() => {
                                setSelectedStaff(member);
                                resetPayrollForm();
                                setPayrollDialog(true);
                              }}>
                                <CalculateIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="textSecondary">
                          {Array.isArray(staff) ? t('salaryPage.staff.noStaff') : t('salaryPage.staff.loadingStaff')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </Box>
            </>
          )}
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
            <Typography variant="h6">{t('salaryPage.payrollTab.title')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                label={t('salaryPage.payrollTab.month')}
                type="month"
                value={monthlyReportMonth}
                onChange={(e) => setMonthlyReportMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadMonthlyPayrollPdf}>
                {t('salaryPage.payrollTab.downloadMonthlyPdf')}
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('salaryPage.table.staffMember')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.month')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.year')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.taxRate')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.deductions')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.netSalary')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.status')}</TableCell>
                <TableCell>{t('salaryPage.payrollTab.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payrollRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.User?.name || record.user?.name || record.staff?.name || record.staffId}</TableCell>
                  <TableCell>{record.month}</TableCell>
                  <TableCell>{record.year}</TableCell>
                  <TableCell>{record.taxRate != null ? `${record.taxRate}%` : ''}</TableCell>
                  <TableCell>{formatXAF(record.deductions || 0)}</TableCell>
                  <TableCell>{formatXAF(record.netSalary || 0)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={record.status} 
                      color={record.status === 'processed' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                                      <TableCell>
                      <Box>
                        <Tooltip title={t('salaryPage.payrollTab.markPaid')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={record.status === 'processed'}
                              onClick={async () => {
                                try {
                                  await fetchJson(`/api/payroll/${record.id}/mark-paid`, { method: 'PATCH' });
                                  setSuccess(t('salaryPage.payrollTab.markedPaid'));
                                  fetchData();
                                } catch (e) {
                                  const msg = e?.data?.error || e?.data?.message || e?.message || t('salaryPage.payrollTab.markPaidFailed');
                                  setError(String(msg));
                                }
                              }}
                            >
                              <CalculateIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </Paper>
      )}

      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('salaryPage.bulk.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Chip label={t('salaryPage.staff.selected', { count: selectedIds.length })} />
            <Chip color="primary" label={t('salaryPage.staff.total', { amount: formatXAF(totalSelectedAmount) })} />
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('salaryPage.bulk.setupPaymentMethodsInfo')}
          </Alert>
          <TextField
            fullWidth
            label={t('salaryPage.payrollTab.month')}
            type="month"
            value={bulkMonth}
            onChange={(e) => setBulkMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="provider-label">{t('salaryPage.bulk.paymentMethod')}</InputLabel>
            <Select
              labelId="provider-label"
              label={t('salaryPage.bulk.paymentMethod')}
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              <MenuItem value="OFFLINE">{t('salaryPage.bulk.offline')}</MenuItem>
              {payoutAvailability.mtn && <MenuItem value="MTN">MTN</MenuItem>}
              {payoutAvailability.orange && <MenuItem value="ORANGE">ORANGE</MenuItem>}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={t('salaryPage.bulk.taxRate') || 'Tax rate % (applied to permanent staff)'}
            type="number"
            value={bulkTaxRate}
            onChange={(e) => setBulkTaxRate(normalizeNumberInput(e.target.value))}
            inputProps={{ min: 0, max: 100, step: '0.01' }}
            sx={{ mt: 2 }}
            helperText={t('salaryPage.bulk.taxRateHelp') || 'Optional. Leave blank for no tax. Part-time staff use their processed payroll.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(false)}>{t('actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={selectedIds.length === 0}
            onClick={async () => {
              try {
                if (!bulkMonth) {
                  setError(t('salaryPage.bulk.selectMonth'));
                  return;
                }
                await fetchJson('/api/payroll/disbursements/bulk', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: { staffIds: selectedIds, provider, month: bulkMonth, taxRate: bulkTaxRate === '' ? undefined : Number(bulkTaxRate) }
                });
                setSuccess(t('salaryPage.bulk.initiated'));
                setBulkDialog(false);
                setSelectedIds([]);
              } catch (e) {
                setError(t('salaryPage.bulk.failed'));
              }
            }}
          >
            {t('salaryPage.bulk.disburse')}
          </Button>
        </DialogActions>
        </Dialog>

      <Dialog open={staffDialog} onClose={() => { setStaffDialog(false); setEditingStaff(null); resetStaffForm(); setStaffDialogRoleId(''); setStaffDialogOptions([]); }} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStaff ? t('salaryPage.staffDialog.editTitle') : t('salaryPage.staffDialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!editingStaff && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="staff-role-select-label">{t('salaryPage.staff.position')}</InputLabel>
                  <Select
                    labelId="staff-role-select-label"
                    label={t('salaryPage.staff.position')}
                    value={staffDialogRoleId}
                    onChange={async (e) => {
                      const roleId = e.target.value;
                      setStaffDialogRoleId(roleId);
                      setStaffForm((prev) => ({
                        ...prev,
                        userId: '',
                        name: '',
                        position: (positions.find((p) => String(p.id) === String(roleId))?.name || ''),
                        mtnMomoPhone: '',
                        orangeMoneyPhone: '',
                      }));
                      try {
                        const staffData = await fetchJson(`/api/payroll/staff?roleId=${encodeURIComponent(String(roleId))}`);
                        const staffRows = (() => {
                          if (Array.isArray(staffData)) return staffData;
                          if (staffData && Array.isArray(staffData.rows)) return staffData.rows;
                          if (staffData && Array.isArray(staffData.data)) return staffData.data;
                          if (staffData && Array.isArray(staffData.staff)) return staffData.staff;
                          if (staffData && staffData.success && Array.isArray(staffData.data)) return staffData.data;
                          return [];
                        })();
                        setStaffDialogOptions(staffRows);
                      } catch (_) {
                        setStaffDialogOptions([]);
                      }
                    }}
                  >
                    {Array.isArray(positions) && positions.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="staff-select-label">{t('salaryPage.staffDialog.staff')}</InputLabel>
                <Select
                  labelId="staff-select-label"
                  label={t('salaryPage.staffDialog.staff')}
                  value={staffForm?.userId || ''}
                  disabled={!!editingStaff}
                  onChange={(e) => {
                    const selId = e.target.value;
                    const source = !editingStaff ? staffDialogOptions : staff;
                    const sel = (Array.isArray(source) ? source : []).find(s => String(s.id) === String(selId));
                    const detected = detectMomoCarrier(sel?.phone);
                    setStaffForm((prev) => ({
                      ...prev,
                      userId: selId,
                      name: sel?.name || '',
                      position: prev.position || sel?.position || '',
                      employmentType: sel?.employmentType || prev.employmentType || 'PERMANENT',
                      basicSalary: prev.basicSalary || sel?.basicSalary || '',
                      hourlyRate: prev.hourlyRate || sel?.hourlyRate || '',
                      mtnMomoPhone: detected?.carrier === 'MTN' ? detected.phone : (detected ? '' : (prev.mtnMomoPhone || sel?.mtnMomoPhone || '')),
                      orangeMoneyPhone: detected?.carrier === 'ORANGE' ? detected.phone : (detected ? '' : (prev.orangeMoneyPhone || sel?.orangeMoneyPhone || '')),
                    }));
                  }}
                >
                  {Array.isArray((!editingStaff ? staffDialogOptions : staff)) && (!editingStaff ? staffDialogOptions : staff).map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('salaryPage.staff.position')}
                value={staffForm.position}
                onChange={(e) => setStaffForm({ ...staffForm, position: e.target.value })}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="employment-type-label">{t('salaryPage.staffDialog.employmentType')}</InputLabel>
                <Select
                  labelId="employment-type-label"
                  label={t('salaryPage.staffDialog.employmentType')}
                  value={staffForm.employmentType}
                  onChange={(e) => setStaffForm({ ...staffForm, employmentType: e.target.value })}
                >
                  <MenuItem value="PERMANENT">{t('salaryPage.staffDialog.permanent')}</MenuItem>
                  <MenuItem value="PART_TIME">{t('salaryPage.staffDialog.partTime')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={String(staffForm.employmentType || '').toUpperCase() === 'PART_TIME' ? t('salaryPage.staffDialog.hourlyRate') : t('salaryPage.staffDialog.basicSalary')}
                type="number"
                value={String(staffForm.employmentType || '').toUpperCase() === 'PART_TIME' ? staffForm.hourlyRate : staffForm.basicSalary}
                onChange={(e) => {
                  const v = normalizeNumberInput(e.target.value);
                  if (String(staffForm.employmentType || '').toUpperCase() === 'PART_TIME') {
                    setStaffForm({ ...staffForm, hourlyRate: v });
                  } else {
                    setStaffForm({ ...staffForm, basicSalary: v });
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">XAF</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('salaryPage.staffDialog.mtnMomoPhone')}
                value={staffForm.mtnMomoPhone}
                onChange={(e) => setStaffForm({ ...staffForm, mtnMomoPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('salaryPage.staffDialog.orangeMoneyPhone')}
                value={staffForm.orangeMoneyPhone}
                onChange={(e) => setStaffForm({ ...staffForm, orangeMoneyPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('salaryPage.staffDialog.statusLabel')}</InputLabel>
                <Select
                  value={staffForm.status}
                  onChange={(e) => setStaffForm({ ...staffForm, status: e.target.value })}
                  label={t('salaryPage.staffDialog.statusLabel')}
                >
                  <MenuItem value="active">{t('salaryPage.staffDialog.statusActive')}</MenuItem>
                  <MenuItem value="inactive">{t('salaryPage.staffDialog.statusInactive')}</MenuItem>
                  <MenuItem value="terminated">{t('salaryPage.staffDialog.statusTerminated')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setStaffDialog(false); setEditingStaff(null); resetStaffForm(); }}>{t('actions.cancel')}</Button>
          <Button onClick={handleStaffSubmit} variant="contained">
            {editingStaff ? t('salaryPage.staffDialog.update') : t('salaryPage.addStaffPayroll')}
          </Button>
        </DialogActions>
        </Dialog>
      

      <Dialog open={payrollDialog} onClose={() => { setPayrollDialog(false); setSelectedStaff(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedStaff?.name ? t('salaryPage.payrollDialog.title', { name: selectedStaff.name }) : t('salaryPage.payrollDialog.titleDefault')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="payroll-staff-select-label">{t('salaryPage.payrollDialog.staff')}</InputLabel>
                <Select
                  labelId="payroll-staff-select-label"
                  label={t('salaryPage.payrollDialog.staff')}
                  value={selectedStaff?.id || payrollForm.staffId || ''}
                  onChange={(e) => {
                    const selId = e.target.value;
                    const sel = payrollStaffSource.find((s) => String(s.id) === String(selId));
                    setSelectedStaff(sel || null);
                    setPayrollForm({ ...payrollForm, staffId: selId });
                  }}
                  disabled={!!selectedStaff}
                >
                  {payrollStaffLoading && (
                    <MenuItem value="" disabled>{t('salaryPage.payrollDialog.loadingStaff')}</MenuItem>
                  )}
                  {Array.isArray(payrollStaffSource) && payrollStaffSource.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('salaryPage.payrollDialog.month')}
                type="month"
                value={payrollForm.month}
                onChange={(e) => setPayrollForm({ ...payrollForm, month: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('salaryPage.payrollDialog.taxRate')}
                type="number"
                value={payrollForm.taxRate}
                onChange={(e) => setPayrollForm({ ...payrollForm, taxRate: normalizeNumberInput(e.target.value) })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            {String(selectedStaff?.employmentType || '').toUpperCase() === 'PART_TIME' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('salaryPage.payrollDialog.workingHours')}
                  type="number"
                  value={payrollForm.approvedHoursWorked}
                  onChange={(e) => setPayrollForm({ ...payrollForm, approvedHoursWorked: normalizeNumberInput(e.target.value) })}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('salaryPage.payrollDialog.manualDeductions')}
                type="number"
                value={payrollForm.manualDeductions}
                onChange={(e) => setPayrollForm({ ...payrollForm, manualDeductions: normalizeNumberInput(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">XAF</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">{t('salaryPage.payrollDialog.infoMessage')}</Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPayrollDialog(false); setSelectedStaff(null); }}>{t('actions.cancel')}</Button>
          <Button onClick={handlePayrollSubmit} variant="contained">
            {t('salaryPage.processPayroll')}
          </Button>
        </DialogActions>
        </Dialog>
      
    </Box>
  );
};

export default SalaryManagementPage;
